import { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { selectGetAuthData } from "@/util/selectors";
import { EmailOtpPanel } from "./emailOtpPanel";
import { PasswordPanel } from "./passwordPanel";
import { sendLoginOtp } from "@/redux/actions/authConfig.action";
import { useCountryShort, useLanguage } from "@/hooks";
import { verifyAndAuthenticateOtpUser } from "@/redux/actions/homepage.action";
import { useTranslation } from "react-i18next";

export const StandardMode: React.FC<any> = ({
  setActiveTab,
  activeTab,
  onEditEmail,
  onSwitchToMobile,
  onAuthMissingFieldsError,
  onAuthCallback,
  withCartRedirect
}) => {
  const dispatch = useAppDispatch();

  const {
    email,
    token: authToken,
    signInOptions,
    emailOtpRetryAfterSeconds
  } = useAppSelector(selectGetAuthData);
  const { t } = useTranslation("auth");

  const canPassword = signInOptions?.includes("password");
  const canEmailOtp = signInOptions?.includes("emailOtp");

  const [emailOtpBooting, setEmailOtpBooting] = useState(false);
  const [otpResendCount, setOtpResendCount] = useState(0);
  const [otpLastSentAt, setOtpLastSentAt] = useState<number | null>(null);
  const [otpRetryAfter, setOtpRetryAfter] = useState<number>(
    emailOtpRetryAfterSeconds
  );
  const [emailOtpBootError, setEmailOtpBootError] = useState<string>("");
  const [otpToken, setOtpToken] = useState(null);

  const bootedRef = useRef(false);
  const inFlightRef = useRef(false);
  const language = useLanguage();
  const countrySlug = useCountryShort();

  const callSendEmailOtp = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setEmailOtpBooting(true);
    try {
      setEmailOtpBootError("");
      const res = await dispatch(
        sendLoginOtp({ token: authToken, channel: "email" })
      );
      const retry = res?.retryAfterSeconds ?? emailOtpRetryAfterSeconds ?? 60;
      setOtpRetryAfter(retry);
      setOtpLastSentAt(Date.now());
      setOtpToken(res.emailOtpToken);
      setOtpResendCount(c => (c === 0 ? 1 : c));
      if (!res?.success) {
        setEmailOtpBootError(res?.message || "Error while sending OTP code");
      }
    } catch (e: any) {
      console.error("Error while sending otp", e);
    } finally {
      setEmailOtpBooting(false);
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (activeTab !== "emailOtp") return;
    if (!canEmailOtp || !authToken) return;
    if (bootedRef.current) return;

    bootedRef.current = true;
    callSendEmailOtp();
  }, [activeTab, canEmailOtp, authToken]);

  const expiresAtMs = useMemo(() => {
    if (!otpLastSentAt) return null;
    return otpLastSentAt + otpRetryAfter * 1000;
  }, [otpLastSentAt, otpRetryAfter]);

  return (
    <div>
      {canPassword && canEmailOtp && (
        <div className="signin_tabs" role="tablist" aria-label="Sign in method">
          {canPassword && (
            <button
              role="tab"
              aria-selected={activeTab === "password"}
              className={cn("tab_btn", { active: activeTab === "password" })}
              onClick={() => setActiveTab("password")}
            >
              {t("passwordTab")}
            </button>
          )}
          {canEmailOtp && (
            <button
              role="tab"
              aria-selected={activeTab === "emailOtp"}
              className={cn("tab_btn", { active: activeTab === "emailOtp" })}
              onClick={() => setActiveTab("emailOtp")}
            >
              {t("emailOtpTab")}
            </button>
          )}
        </div>
      )}

      {activeTab === "password" && canPassword && (
        <PasswordPanel
          email={email}
          onEditEmail={onEditEmail}
          onAuthMissingFieldsError={onAuthMissingFieldsError}
          onAuthCallback={onAuthCallback}
        />
      )}

      {activeTab === "emailOtp" && canEmailOtp && (
        <EmailOtpPanel
          otpToken={otpToken}
          email={email}
          loading={emailOtpBooting}
          sendOtpCount={otpResendCount}
          expiresAtMs={expiresAtMs}
          onEditEmail={onEditEmail}
          onSwitchToMobile={onSwitchToMobile}
          initialErrorMessage={emailOtpBootError}
          requestEmailOtp={async () => {
            const res = await dispatch(
              sendLoginOtp({ token: authToken, channel: "email" })
            );
            if (!res?.success) {
              throw new Error(res?.message || "Error while sending OTP code");
            }
            const retry = res?.retryAfterSeconds;
            setOtpToken(res.emailOtpToken);
            setOtpRetryAfter(retry);
            setOtpLastSentAt(Date.now());
            setOtpResendCount(c => c + 1);
            setEmailOtpBootError("");
            return retry;
          }}
          verifyEmailOtp={async (code: string) => {
            const resp: any = await dispatch(
              verifyAndAuthenticateOtpUser({
                code,
                token: otpToken,
                history,
                language,
                country: countrySlug,
                redirect: false,
                onAuth: onAuthCallback,
                withCartRedirect,
                onAuthMissingFieldsError: onAuthMissingFieldsError
              })
            );
            const success = Boolean(resp?.success) || resp?.status === 200;
            const message = resp?.message || "";
            return { success, message };
          }}
        />
      )}
    </div>
  );
};
